import { CarPdfChart, CarPdfChartProps } from "@app/components/PdfChart";
import { CarPortfolioChartItem } from "@app/pages/assetAllocation/asset-allocation-types";
import { useTheme } from "@mui/material";
import { ChartConfiguration, FontSpec } from "chart.js";
import { ChartPlugins, ChartUtils } from "@app/utils/chartUtils";
import { CarPdfBox } from "@app/components/PdfBox";
import { CarPdfChartLegend } from "@app/components/PdfChartLegend";
import { formatNumber } from "@app/utils";
import {
  getIndexCompareData,
  IndexCompareItem,
} from "@app/components/ChartIndexCompare";
import { addYears } from "date-fns";

interface IndexComparePdfChartProps
  extends Omit<CarPdfChartProps, "type" | "options" | "data" | "plugins"> {
  item1?: IndexCompareItem;
  item2?: IndexCompareItem;
  item3?: IndexCompareItem;
  isWhiteContext?: boolean;
}

export const IndexComparePdfChart = ({
  item1,
  item2,
  item3,
  isWhiteContext,
  ...props
}: IndexComparePdfChartProps) => {
  const theme = useTheme();

  const { datasets, xLabel, yLabel } = getIndexCompareData({
    theme,
    item1,
    item2,
    item3,
  });
  console.log({ item1, item2, item3, datasets, xLabel, yLabel });

  const font: Partial<FontSpec> = {
    size: 11,
    weight: 400,
    lineHeight: 1,
    family: theme.typography.fontFamily,
  };

  const titleFont: Partial<FontSpec> = {
    ...font,
    size: 12,
    weight: 600,
  };

  return (
    <CarPdfBox>
      <CarPdfChart
        type="line"
        data={{ datasets }}
        options={{
          maintainAspectRatio: false,
          scales: {
            x: {
              type: "time",
              time: {
                unit: "year",
              },
              ticks: {
                font,
                color: theme.palette.softBlack,
              },
              grid: {
                display: false,
              },
            },
            y: {
              title: {
                text: yLabel,
                display: true,
                font: titleFont,
              },
              ticks: {
                font,
                color: theme.palette.softBlack,
                callback: (value) =>
                  typeof value === "number"
                    ? formatNumber(value, {
                        decimalPlaces: 2,
                        forceShowDecimals: true,
                      })
                    : value,
              },
              grid: {
                display: true,
                color: (ctx) => {
                  return ctx.tick.value === 0
                    ? theme.palette.gray7
                    : theme.palette.gray2;
                },
                lineWidth: 1,
                tickColor: theme.palette.white,
              },
            },
          },
          plugins: {
            roundedBackground: ChartUtils.getRoundedBackgroundOptions({
              theme,
              isWhiteContext,
            }),
            periods: {
              backgroundColor: "rgba(236, 238, 240, 0.5)",
              borderColor: "rgba(178, 179, 181, 0.5)",
              font: {
                ...font,
                style: "italic",
                size: 8,
              },
              textColor: theme.palette.black,
              labelPosition: "horizontal",
              items: [
                {
                  start: addYears(new Date(), -10).getTime(),
                  end: new Date().getTime(),
                  label: "Timeframe too short for 10 year analysis",
                },
              ],
            },
          },
        }}
        plugins={[ChartPlugins.roundedBackground, ChartPlugins.periods]}
        {...props}
      />
      <CarPdfChartLegend
        style={{ marginTop: 16 }}
        items={[item1, item2, item3]
          .filter((i): i is IndexCompareItem => !!i)
          .map((item, idx) => {
            const color =
              idx === 0
                ? theme.palette.chartsColor.blue
                : idx === 1
                  ? theme.palette.chartsColor.orange
                  : theme.palette.chartsColor.green;
            return [
              {
                label: `${item.name} Historic Line`,
                color: color,
              },
              {
                label: `${item.name} Trend Line`,
                color: color,
                isDottedLine: true,
              },
            ];
          })
          .flat()}
      />
    </CarPdfBox>
  );
};
